import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { usePostHog } from 'posthog-js/react';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { getDeviceInfo } from 'state/localStorageTyped';
import {
  ProcessPaymentState,
  ProcessMembershipPaymentTokenData,
  ProcessMembershipUpgradeData,
} from 'shared/types';
import {
  useMembershipState,
  MEMBERSHIP_CODES,
  MEMBERSHIP_DESCRIPTION_TRANS_KEYS,
  MEMBERSHIP_TITLE_TRANS_KEYS,
  MembershipState,
} from 'state/useMembershipState';
import { RecurlyAccountInformation } from 'features/payments/components/RecurlyAccountInformation';
import { useSavedPaymentMethod } from 'hooks/useSavedPaymentMethod';
import { setAnalyticsUser } from 'shared/analytics';
import { useAuthState } from 'state/useAuthState';
import { usePaymentApi } from 'hooks/usePaymentApi';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { ENV } from 'shared/ENV';
import { MEMBERSHIP_PLANS_AMOUNTS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import { useMembershipAccountInfoQueryParams } from 'features/membership/hooks/useMembershipUrlParams';
import { formatToUSDollars } from 'shared/utils';
import { Loader } from 'components/Loader';

const getInitialState = (
  membershipState: MembershipState,
  paramValues: {
    email: string;
    firstName?: string;
    lastName?: string;
  },
): ProcessPaymentState => {
  const {
    clientToken,
    email,
    firstName,
    lastName,
    optInToUpdates,
    existingUser,
    verificationToken,
    userId,
    plan,
  } = membershipState;

  return {
    email: email || paramValues.email,
    firstName: firstName || paramValues.firstName || '',
    lastName: lastName || paramValues.lastName || '',
    clientToken,
    optInToUpdates,
    existingUser,
    transactionAmount: MEMBERSHIP_PLANS_AMOUNTS[plan],
    verificationToken,
    userId,
  };
};

const Content = (): JSX.Element | null => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user } = useAuthState();
  const posthog = usePostHog();

  const {
    clientToken: clientTokenParam,
    email: emailParam,
    verificationToken: verificationTokenParam,
    userId: userIdParam,
    plan,
    firstName,
    lastName,
    upgrade,
    posthogTrackingId: posthogIdParam,
  } = useMembershipAccountInfoQueryParams();

  useEffect(() => {
    // set the user id for analytics when purchase membership flow continues on web
    if (userIdParam) {
      setAnalyticsUser({ userId: userIdParam.toString() });
    }
    // Re-identify with PostHog if coming from mobile app
    if (posthogIdParam) {
      posthog.identify(posthogIdParam);
    }
  }, [userIdParam, posthogIdParam, posthog]);

  const stateUpdateData: Partial<MembershipState> | undefined = useMemo(() => {
    return clientTokenParam || verificationTokenParam
      ? {
          clientToken: clientTokenParam,
          verificationToken: verificationTokenParam,
          userId: userIdParam,
          existingUser: Boolean(emailParam),
          fromMobile: true,
          plan,
          upgrade,
        }
      : undefined;
  }, [
    clientTokenParam,
    emailParam,
    plan,
    upgrade,
    userIdParam,
    verificationTokenParam,
  ]);

  const { updateMembershipState, ...membershipState } = useMembershipState({
    stateUpdateData,
  });

  const isMembershipUpgrade =
    upgrade ||
    membershipState.upgrade ||
    user?.supporterStatus?.planType === 'regular';

  const initialState = getInitialState(membershipState, {
    email: emailParam,
    firstName,
    lastName,
  });

  const userId = membershipState.userId || userIdParam;
  const verificationToken =
    membershipState.verificationToken || verificationTokenParam;

  const { savedPaymentMethod, isLoading: isLoadingPaymentMethod } =
    useSavedPaymentMethod({ userId, verificationToken });

  const { processMembershipPaymentMutation } = usePaymentApi<
    ProcessMembershipPaymentTokenData | ProcessMembershipUpgradeData
  >({
    onSuccess: () => {
      let redirectPath = '/membership/success';
      if (isMembershipUpgrade) {
        redirectPath += '?upgrade=true';
      }
      history.push(redirectPath);
    },
  });

  if (isLoadingPaymentMethod) {
    return <Loader disableBackground />;
  }

  if (!initialState.transactionAmount) {
    // Something has gone horribly wrong, we'll reset their local storage, restart the process, and hope for the best
    Sentry.captureMessage('Membership transaction amount unset', {
      level: 'warning',
    });
    membershipState.resetMembershipState();
    const paywallLink = getPaywallLink({
      hasActiveMembership: false,
      source: 'membership_account_info_error',
    });
    history.push(paywallLink.url);
    enqueueSnackbar(t('membershipCheckout.unknownError'), { variant: 'error' });
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{t('membershipAccountInformation.title')}</title>
      </Helmet>
      <SimplePageLayout>
        <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
          <Elements>
            <RecurlyAccountInformation<
              ProcessMembershipPaymentTokenData | ProcessMembershipUpgradeData
            >
              transactionType="membership"
              initialState={initialState}
              updateState={updateMembershipState}
              processPaymentMutation={processMembershipPaymentMutation}
              paymentMethod={savedPaymentMethod}
              checkoutConfig={{
                code: MEMBERSHIP_CODES[membershipState.plan],
                title: t(MEMBERSHIP_TITLE_TRANS_KEYS[membershipState.plan]),
                description: t(
                  MEMBERSHIP_DESCRIPTION_TRANS_KEYS[membershipState.plan],
                  {
                    price: formatToUSDollars(
                      initialState.transactionAmount || 0,
                      2,
                    ),
                  },
                ),
                upgrade: isMembershipUpgrade,
              }}
            />
          </Elements>
        </RecurlyProvider>
      </SimplePageLayout>
    </>
  );
};

export const MembershipAccountInformation = (): JSX.Element => {
  const { t } = useTranslation();
  const deviceInfo = getDeviceInfo();
  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  return (
    <PageLayout
      data-cname="MembershipAccountInformationPage"
      allowAppBanner={false}
      headerBarProps={{
        action: isMobilePlatform ? 'noControls' : 'back',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
